import { useMemo, type FC, useEffect } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router-dom'
import { ContainerHeader, Scrollbar, Space, ToggleBlock } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { type ContactsDetailsStore } from 'widgets/ContactsDetails/store/contactsDetailsStore'
import { ContactsContainerHeader } from 'widgets/ContactsDetails/ui/ContactsContainerHeader/ContactsContainerHeader'
import { ContactsProfileCard } from 'widgets/ContactsDetails/ui/ContactsProfileCard'
import { ContactsGroupCard } from 'widgets/ContactsDetails/ui/ContactsGroupCard/ui/ContactsGroupCard'
import { ContactsNotesForm } from 'widgets/ContactsDetails/ui/ContactsNotesForm'
import { ContactsIntegrations } from 'widgets/ContactsDetails/ui/ContactsIntegrations'
import { ContactsQuickLinksSearch } from 'widgets/ContactsDetails/ui/ContactsQuickLinks'
import { ContactsConversationsHistory } from 'widgets/ContactsDetails/ui/ContactsConversationsHistory/ui/ContactsConversationsHistory'
import { ContactsContentItems } from 'widgets/ContactsDetails/ui/ContactsContentItems/ContactsContentItems'
import { ContactsConversationOpen } from 'widgets/ContactsDetails/ui/ContactsConversationOpen'
import {
  useContactsDetailsContext,
  ContactsDetailsContext,
} from 'widgets/ContactsDetails/context/contactsDetailsContext'
import styles from 'widgets/ContactsDetails/ui/styles.module.scss'

const ContactsDetailsContent = observer(() => {
  const contactsDetailsStore = useContactsDetailsContext()
  const { organizationStore } = useEntitiesContext()
  const {
    isConversationGroup,
    isViewAll,
    selectedContact,
    contactId,
    isViewTypeNotes,
    isViewTypeConversationsHistory,
    isViewTypeQuickLinks,
    contactNoteStore,
    isVariantCreateContactModal,
    contactsProfileCardStore,
    contactsQuickLinksStore,
    contactsGroupCardStore,
    contactIntegrationsStore,
    conversationsHistoryMessagesStore,
    config,
    hasAlert,
  } = contactsDetailsStore
  const { isCollapse, isShowContactDetailsOverlay, handleToggleCollapse } =
    contactsDetailsGlobalStore

  const { state } = useLocation()
  const inboxId = state?.inboxId

  useEffect(() => {
    if (inboxId) contactsDetailsStore.setInboxId(inboxId)

    contactsDetailsStore.load()

    return () => {
      contactsDetailsStore.dispose()
    }
  }, [])

  useEffect(() => {
    if (!organizationStore.isActivityLoggingEnabled) return

    const isCollapseOpened = isCollapse && !isVariantCreateContactModal
    if (contactId !== null && contactId > 0 && !isCollapseOpened && !isVariantCreateContactModal) {
      organizationStore.handleTrackOrganizationActivity({
        event: 'phi.contact.viewed',
        data: { contact_id: contactId },
      })
    }
  }, [contactId, organizationStore.isActivityLoggingEnabled])

  const contact = selectedContact

  const handleBackNote = () => {
    contactNoteStore.messageFieldStore.clear()
  }

  const createNewContactMode = contact?.id === -1

  const renderHeader = () => {
    if (createNewContactMode && !isVariantCreateContactModal) {
      return (
        <ContainerHeader
          title='Create'
          isBorderBottom
          paddingLeft={20}
          actions={[
            {
              icon: 'close',
              text: 'Cancel',
              onAction: () => {
                config?.onStopCreateNewContact?.()
                handleToggleCollapse()
              },
            },
          ]}
          {...config?.containerHeaderProps}
        />
      )
    }

    if (isViewTypeNotes) {
      return <ContactsContainerHeader backCallback={handleBackNote} />
    }

    if (contact?.id === 0 || isViewTypeConversationsHistory) {
      return <ContactsContainerHeader />
    }

    if (isViewAll) {
      return (
        <ContactsContainerHeader
          titleCount={contactIntegrationsStore.viewAllCount}
          customTitleIcon={contactIntegrationsStore.customTitleIcon}
          showCount
        />
      )
    }

    if (isVariantCreateContactModal) return null

    return (
      <ContactsContainerHeader
        backCallback={() => {
          config?.onCloseContactDetails?.()
        }}
      />
    )
  }
  const renderContent = () => {
    if (contact?.id === 0) {
      return (
        <>
          <ContactsProfileCard store={contactsProfileCardStore} />
          <Space height={60} />
        </>
      )
    }

    if (isConversationGroup) {
      return (
        <div className={styles.infoWrapper}>
          <ContactsProfileCard store={contactsProfileCardStore} />

          {contactsGroupCardStore.isCurrentContact ? (
            <ContactsContentItems />
          ) : (
            <ToggleBlock
              borders={{ bottom: true }}
              disabled={createNewContactMode}
              hideDraggingIcon={true}
              isOpen={contactsDetailsGlobalStore.isOpenBlock('contactsGroup')}
              onToggle={() => contactsDetailsGlobalStore.toggleBlockView('contactsGroup')}
              title='Contacts'
              padding={'0 11px 23px'}
              margin={'-3px 0 0'}
            >
              <ContactsGroupCard store={contactsGroupCardStore} />
            </ToggleBlock>
          )}

          <Space height={60} />
        </div>
      )
    }

    if (isViewTypeNotes) {
      return (
        <>
          <ContactsNotesForm fullView store={contactNoteStore} />
          <Space height={60} />
        </>
      )
    }

    if (isViewAll) {
      return (
        <>
          <ContactsIntegrations store={contactIntegrationsStore} />
          <Space height={60} />
        </>
      )
    }

    if (isViewTypeQuickLinks) {
      return <ContactsQuickLinksSearch store={contactsQuickLinksStore} />
    }

    if (isViewTypeConversationsHistory) {
      return (
        <ContactsConversationsHistory
          store={conversationsHistoryMessagesStore}
          isModalView={isVariantCreateContactModal}
        />
      )
    }

    if (isVariantCreateContactModal) {
      return (
        <div className={styles.infoWrapper}>
          <ContactsProfileCard store={contactsProfileCardStore} />
          <ContactsContentItems />
        </div>
      )
    }

    return (
      <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={'100%'} height={'100%'}>
        <div className={styles.infoWrapper}>
          <ContactsProfileCard store={contactsProfileCardStore} />
          <ContactsContentItems />
          <Space height={60} />
        </div>
      </Scrollbar>
    )
  }
  const renderLeftContent = () => {
    if (contactsDetailsStore.isVariantOpenConversations) {
      if (!contactsDetailsStore.contactsConversationOpenStore) return null

      return <ContactsConversationOpen store={contactsDetailsStore.contactsConversationOpenStore} />
    }

    return null
  }
  const isMobileOpened = isShowContactDetailsOverlay && !isVariantCreateContactModal
  const isCollapseOpened = isCollapse && !isVariantCreateContactModal
  const isMobileFixed = isMobileOpened && !isCollapse && config?.variantPosition === 'fixed'

  return (
    <div
      className={classnames(styles.wrap, {
        [styles.isCollapse]: isCollapseOpened,
        [styles.isMobileOpened]: isMobileOpened,
        [styles['isMobileOpened--fixed']]: isMobileFixed,
        [styles.isConversationsHistoryView]: isViewTypeConversationsHistory,
        [styles.modalView]: isVariantCreateContactModal,
        [styles.hideModalViewBorderTop]: hasAlert,
      })}
    >
      <div className={styles.header}>{renderHeader()}</div>
      <div className={styles.content}>{renderContent()}</div>
      <div className={styles.leftContent}>{renderLeftContent()}</div>
    </div>
  )
})

type IContactsDetailsProps = {
  store: ContactsDetailsStore
}

export const ContactsDetails: FC<IContactsDetailsProps> = observer(({ store }) => {
  return useMemo(() => {
    return (
      <ContactsDetailsContext.Provider value={store}>
        <ContactsDetailsContent key={store.id} />
      </ContactsDetailsContext.Provider>
    )
  }, [store.contactsIds])
})
